import { useEffect, useState, type RefObject } from 'react';
import { WorkflowCanvas, type MinimapData, type MobileGestureHandlers } from '@/components/editor/WorkflowCanvas';
import { ActionLogPanel } from '@/components/workflow/ActionLogPanel';
import { subscribeOnboardingUI } from '@/components/onboarding/uiAdapter';
import { cn } from '@/lib/utils';
import type {
  WorkflowBlock,
  Connector,
  BlockGroup as Group,
  PortSide,
} from '@/types/workflow';
import type { ActionEntry } from '@/hooks/useActionLog';

export interface CanvasAreaProps {
  viewOnly: boolean;
  sidebarLeft: React.ReactNode;
  sidebarRight: React.ReactNode;
  actionLogEntries: ActionEntry[];
  onClearActionLog: () => void;
  /** Forward to WorkflowCanvas — mount the movable search+zoom HUD. */
  enableFloatingHud?: boolean;

  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: Group[];
  selectedBlockId: string | null;
  selectedGroupId: string | null;
  connectMode: boolean;
  groupMode: boolean;
  groupSelection: string[];
  visibleBlocks: WorkflowBlock[];
  visibleConnectors: Connector[];
  collapsedGroups: Group[];
  expandedGroups: Group[];
  breadcrumbs: { id: string | null; name: string }[];
  layerPath: string[];

  onNavigateInto: (id: string) => void;
  onNavigateToLevel: (level: number) => void;
  onSelectBlock: (id: string) => void;
  onSelectGroup: (id: string | null) => void;
  onCreateBlock: (type: string, x: number, y: number, nameOverride?: string) => void;
  onUpdateBlockPosition: (id: string, x: number, y: number) => void;
  onUpdateBlockIcon: (id: string, iconKey: string) => void;
  onConnect: (fromId: string, toId: string, fromPort?: PortSide, toPort?: PortSide) => void;
  onToggleGroupSelection: (id: string) => void;
  onCanvasClick: () => void;
  onMinimapDataChange: (d: MinimapData) => void;
  onUpdateGroupPosition: (id: string, x: number, y: number) => void;
  onConvertToLayer: (id: string) => void;
  onUpdateConnectorWaypoints: (id: string, waypoints: { x: number; y: number }[]) => void;
  onUpdateConnectorLabel: (id: string, label: string) => void;

  // Block context-menu hooks (new right-click menu)
  onFocusBlockProperty?: (blockId: string, field: 'name' | 'description' | 'color') => void;
  onCreateGroupFromSelection?: (blockIds: string[]) => void;
  onAddBlockToGroup?: (groupId: string, blockId: string) => void;
  onDuplicateBlock?: (blockId: string) => void;
  onDeleteBlock?: (blockId: string) => void;
  onDeleteConnector?: (id: string) => void;
  onExplainBlock?: (blockId: string) => void;
  // Layer-card + group-frame context-menu hooks
  onToggleGroupCollapse?: (groupId: string) => void;
  onDeleteGroup?: (groupId: string) => void;
  onToggleGroupLock?: (groupId: string) => void;
  // Tool preset for canvas right-click "Add block" submenu
  toolTypes?: Array<{ type: import('@/types/workflow').BlockType; label: string; icon?: string }>;
  // Cross-sheet linking state forwarded to WorkflowBlock for visual feedback
  crossSheetMode?: boolean;
  crossSheetSourceBlockId?: string | null;

  canvasInnerRef: RefObject<HTMLDivElement>;

  /** Mobile-only gesture-handlers — pass-through till WorkflowCanvas. */
  mobileGestures?: MobileGestureHandlers;
}

/**
 * Desktop canvas area (two collapsible sidebars + canvas + action log).
 * Extracted from Index.tsx so the page composition stays thin.
 */
export function CanvasArea(props: CanvasAreaProps) {
  const { viewOnly, sidebarLeft, sidebarRight } = props;

  // Onboarding can force either sidebar open while a guided step targets it.
  const [forcedLeft, setForcedLeft] = useState(false);
  const [forcedRight, setForcedRight] = useState(false);
  useEffect(() => {
    return subscribeOnboardingUI((d) => {
      if (d.type === 'snapshot') {
        // Sidebars are hover-only; nothing to snapshot beyond the forced flags.
        setForcedLeft(false);
        setForcedRight(false);
      } else if (d.type === 'restore') {
        setForcedLeft(false);
        setForcedRight(false);
      } else if (d.type === 'force-region') {
        if (d.region === 'sidebar-left') setForcedLeft(true);
        if (d.region === 'sidebar-right') setForcedRight(true);
      } else if (d.type === 'release-region') {
        if (d.region === 'sidebar-left') setForcedLeft(false);
        if (d.region === 'sidebar-right') setForcedRight(false);
      }
    });
  }, []);

  return (
    <div className="flex-1 flex overflow-hidden relative" id="canvas" tabIndex={-1}>
      {!viewOnly && (
        <div
          data-focus-region="sidebar-left"
          className="group/left relative z-20 flex-shrink-0 w-[72px] h-full"
        >
          <div
            className={cn(
              'absolute top-0 left-0 h-full overflow-hidden sidebar-gradient transition-all duration-300 ease-in-out',
              forcedLeft ? 'w-80' : 'w-[72px] group-hover/left:w-80',
            )}
          >
            <div className="w-80 h-full">{sidebarLeft}</div>
          </div>
        </div>
      )}

      <WorkflowCanvas
        blocks={props.blocks}
        connectors={props.connectors}
        groups={props.groups}
        selectedBlockId={props.selectedBlockId}
        selectedGroupId={props.selectedGroupId}
        connectMode={props.connectMode}
        groupMode={props.groupMode}
        groupSelection={props.groupSelection}
        visibleBlocks={props.visibleBlocks}
        visibleConnectors={props.visibleConnectors}
        collapsedGroups={props.collapsedGroups}
        expandedGroups={props.expandedGroups}
        breadcrumbs={props.breadcrumbs}
        layerPath={props.layerPath}
        onNavigateInto={props.onNavigateInto}
        onNavigateToLevel={props.onNavigateToLevel}
        onSelectBlock={props.onSelectBlock}
        onSelectGroup={props.onSelectGroup}
        onCreateBlock={props.onCreateBlock}
        onUpdateBlockPosition={props.onUpdateBlockPosition}
        onUpdateBlockIcon={props.onUpdateBlockIcon}
        onConnect={props.onConnect}
        onToggleGroupSelection={props.onToggleGroupSelection}
        onCanvasClick={props.onCanvasClick}
        onMinimapDataChange={props.onMinimapDataChange}
        onUpdateGroupPosition={props.onUpdateGroupPosition}
        onConvertToLayer={props.onConvertToLayer}
        onUpdateConnectorWaypoints={props.onUpdateConnectorWaypoints}
        onUpdateConnectorLabel={props.onUpdateConnectorLabel}
        onFocusBlockProperty={props.onFocusBlockProperty}
        onCreateGroupFromSelection={props.onCreateGroupFromSelection}
        onAddBlockToGroup={props.onAddBlockToGroup}
        onDuplicateBlock={props.onDuplicateBlock}
        onDeleteBlock={props.onDeleteBlock}
        onDeleteConnector={props.onDeleteConnector}
        onExplainBlock={props.onExplainBlock}
        onToggleGroupCollapse={props.onToggleGroupCollapse}
        onDeleteGroup={props.onDeleteGroup}
        onToggleGroupLock={props.onToggleGroupLock}
        toolTypes={props.toolTypes}
        crossSheetMode={props.crossSheetMode}
        crossSheetSourceBlockId={props.crossSheetSourceBlockId}
        canvasInnerRef={props.canvasInnerRef}
        viewOnly={viewOnly}
        mobileGestures={props.mobileGestures}
        enableFloatingHud={props.enableFloatingHud}
      />

      {!viewOnly && (
        <div
          data-focus-region="sidebar-right"
          className="group/right relative z-20 flex-shrink-0 w-[72px] h-full"
        >
          <div
            className={cn(
              'absolute top-0 right-0 h-full overflow-hidden bg-card border-l border-border transition-all duration-300 ease-in-out',
              forcedRight ? 'w-80' : 'w-[72px] group-hover/right:w-80',
            )}
          >
            <div className="w-80 h-full">{sidebarRight}</div>
          </div>
        </div>
      )}

      {!viewOnly && (
        <ActionLogPanel entries={props.actionLogEntries} onClear={props.onClearActionLog} />
      )}
    </div>
  );
}
